import { memo, useContext, useEffect, useRef } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { Route, Routes, useLocation, useMatch } from "react-router-dom";
import { AsyncSmartDPBooking, AsyncSmartDPListingContainer } from "app/routes";
import SmartDPHotelDescriptionContainer from "app/pages/SmartDP/Quote/HotelDescription/SmartDPHotelDescriptionContainer";
import { QUOTATION_CODE_STATUS, WEBSITE_TUNNEL_TYPES } from "app/constants";
import HeaderContainer from "app/pages/Header/HeaderContainer";
import useHidePartnerCustomHeader from "app/utils/hooks/useHidePartnerCustomHeader";

import "./SmartDP.scss";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import AppGlobalsContext from "app/AppGlobalsContext";
import HomeLayoutContainer from "app/pages/HomeLayout/HomeLayoutContainer";
import QueriesParser from "app/pages/.shared/QueriesParser/QueriesParser";
import { BreadcrumbsContainer } from "app/features/breadcrumb";
import { useIntl } from "react-intl";
import { BookingStepperContainer } from "app/features/booking/steps";
import { buildSDPListingUrlFromQuote } from "app/utils/smartDPUtils";

const SmartDP = ({
	homeBlocks = [],
	fetchSDPProductsStatus,
	fetchSDPAvailabilitiesStatus,
	destinationName,
	hotelName,
	fetchSDPFilters = () => {},
}) => {
	const { pathname, search, state: locationState } = useLocation();
	const { resolution } = useContext(AppGlobalsContext);
	const { formatMessage } = useIntl();

	const smartDPRef = useRef(null);

	const isSdpSearchPage = pathname.includes("sdp/search");

	const isSDPSearchPage = useMatch({
		path: "/sdp/search",
		end: true,
		caseSensitive: true,
	});
	const isSDPRootPage = useMatch({
		path: "/",
		end: true,
		caseSensitive: true,
	});

	const headerClass = classNames("smartdp", {
		"smartdp--search": isSDPRootPage || isSDPSearchPage,
	});

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	useHidePartnerCustomHeader({ hide: !isSdpSearchPage && !isSDPRootPage });

	const isLoadingSDPListing = fetchSDPProductsStatus === QUOTATION_CODE_STATUS.LOADING;

	const isLoadingSdpQuote = fetchSDPAvailabilitiesStatus === QUOTATION_CODE_STATUS.LOADING;

	const listingUrl = buildSDPListingUrlFromQuote({
		listingBasePath: "/sdp/listing",
		quoteSearch: search,
		listingView: locationState?.listingView,
	});

	const crumbs = [
		{ label: formatMessage({ id: "faq.breadcrumb.home" }), to: "/" },
		...(destinationName ? [{ label: destinationName, to: listingUrl }] : []),
		...(hotelName ? [{ label: hotelName }] : []),
	];

	useEffect(() => {
		fetchSDPFilters();
	}, []);

	return (
		<div className={headerClass} ref={smartDPRef}>
			<Routes>
				<Route path="/search" element={null} />
				<Route
					path="/hotelDescription/:accommodationId/:code"
					element={
						<HeaderContainer showMenuItems showDialPhone={false} showBackBtn={false} />
					}
				/>
				<Route
					path="/listing/*"
					element={
						<HeaderContainer
							showMenuItems
							showMiniSearch
							showDialPhone={false}
							showBackBtn={isMobile}
							displayLoader={isLoadingSDPListing}
						/>
					}
				/>
				<Route
					path="/listing/map"
					element={
						isMobile ? (
							<></>
						) : (
							<HeaderContainer
								showMenuItems
								showMiniSearch
								showDialPhone={false}
								displayLoader={isLoadingSDPListing}
							/>
						)
					}
				/>
				<Route
					path="/booking/payment"
					element={<HeaderContainer showMenuItems={false} showDialPhone showBackBtn />}
				/>
				<Route
					path="/booking/quote"
					element={
						<>
							<HeaderContainer
								showMenuItems={false}
								showDialPhone
								showBackBtn={isMobile}
								displayLoader={isLoadingSdpQuote}
							/>

							<BreadcrumbsContainer items={crumbs} className="smartdp__breadcrumbs" />
						</>
					}
				/>
				<Route
					path="/booking/*"
					element={<HeaderContainer showMenuItems={false} showDialPhone={false} />}
				/>
				<Route path="/" element={null} />
			</Routes>

			<Routes>
				<Route
					path="/booking/payment"
					element={
						<div className="smartdp__stepper">
							<BookingStepperContainer tunnel="sdp" />
						</div>
					}
				/>

				<Route
					path="/booking/confirmation"
					element={
						<div className="smartdp__stepper">
							<BookingStepperContainer tunnel="sdp" />
						</div>
					}
				/>
			</Routes>

			<Routes>
				<Route
					path="/search"
					element={
						<QueriesParser>
							<HomeLayoutContainer
								homeBlocks={homeBlocks}
								websiteTunnels={[
									{ websiteTunnelType: WEBSITE_TUNNEL_TYPES.HOLIDAYS },
								]}
							/>
						</QueriesParser>
					}
				/>
				<Route
					path="/hotelDescription/:accommodationId/:code"
					element={<SmartDPHotelDescriptionContainer printMode={true} />}
				/>
				<Route
					path="/listing/*"
					element={
						<QueriesParser>
							<AsyncSmartDPListingContainer />
						</QueriesParser>
					}
				/>
				<Route path="/booking/*" element={<AsyncSmartDPBooking />} />
				<Route
					path="/"
					element={
						<QueriesParser>
							<HomeLayoutContainer
								homeBlocks={homeBlocks}
								websiteTunnels={[
									{ websiteTunnelType: WEBSITE_TUNNEL_TYPES.HOLIDAYS },
								]}
							/>
						</QueriesParser>
					}
				/>
			</Routes>
		</div>
	);
};

SmartDP.propTypes = {
	homeBlocks: PropTypes.array,
	fetchSDPProductsStatus: PropTypes.string,
	fetchSDPAvailabilitiesStatus: PropTypes.string,
	fetchSDPFilters: PropTypes.func,
	destinationName: PropTypes.string,
	hotelName: PropTypes.string,
};

export default memo(SmartDP);
